import { RNG } from "rot-js";
import { Point } from "../../../engine/space/bounds";
import { genAllDirections } from "../../../engine/space/directions";
import { createAction } from "../../actions/Action";
import { attackTo } from "../../actions/ActionAttacks";
import { moveTo, wait } from "../../actions/ActionMovement";
import { AttackingParticle } from "../../anims/particles/AttackingParticle";
import { StaggeredParticle } from "../../anims/particles/StaggeredParticle";
import { ParticleManager } from "../../anims/particleSystem";
import { findActorForPosition, gameData } from "../../GameManager";
import { moveOut } from "../../map/Pathfinding";
import { Actor } from "../Actor";
import { getActiveAttack } from "../weaponHelper";

export const updateSeen = (actor: Actor) => {
  const visible = gameData.fov.get(actor.x + actor.y * gameData.current.width);
  if (visible) {
    actor.seenPlayer = 10; //Turns the monster will chase enemy out of line of sight
  } else if (actor.seenPlayer) {
    actor.seenPlayer -= 1;
  }
  return visible;
};

export const staggered = (actor: Actor) => {
  if (actor.status.staggered) {
    actor.addParticle(
      ParticleManager.makeUntilEndOfTurn(
        StaggeredParticle,
        actor.x,
        actor.y,
        actor.breed.fg
      )
    );
    return createAction(wait, actor, null);
  }
};

export const bestMoveTowardsPlayer = (actor: Actor) => {
  const points = gameData.dMapPlayer?.getSortedAdjacent(actor, moveOut);
  if (points) {
    for (let i = 0; i < points.length; i++) {
      const target = points[i];
      const at = findActorForPosition(target.x, target.y);
      if (!at || at === gameData.player) {
        return [target, at] as const;
      }
    }
  }
  return [];
};

export const bestMoveOrAttackPlayer = (actor: Actor) => {
  const [target, at] = bestMoveTowardsPlayer(actor);
  if (target) {
    if (at === gameData.player) {
      const attack = getActiveAttack(actor);
      if (attack.stamina <= actor.stamina.current) {
        addAttackParticle(actor);
        return createAction(attackTo, actor, { target });
      }
    } else if (!at) {
      return createAction(moveTo, actor, { target });
    }
  }
};

export const getMoveAway = (actor: Actor) => {
  const points = gameData.dMapPlayer?.getSortedAdjacent(
    { x: actor.x, y: actor.y },
    moveOut
  );
  if (points) {
    for (let i = points.length - 1; 0 <= i; i--) {
      const target = points[i];
      const at = findActorForPosition(target.x, target.y);
      if (!at) {
        return createAction(moveTo, actor, { target });
      }
    }
  }
};

export const conditionalApproach = (
  actor: Actor,
  distanceOnFail: number,
  approach: boolean
) => {
  let action;
  const distance = getDistance(actor);
  if (distance && distance <= distanceOnFail) {
    if (approach) {
      action = bestMoveOrAttackPlayer(actor);
      if (action) {
        return action;
      }
    } else {
      if (distance === 1) {
        const attack = getActiveAttack(actor);
        if (attack.stamina <= actor.stamina.current) {
          addAttackParticle(actor);
          return createAction(attackTo, actor, {
            target: { x: gameData.player.x, y: gameData.player.y },
          });
        }
      }
      action = getMoveAway(actor);
      if (action) {
        return action;
      }
    }
  } else {
    action = bestMoveOrAttackPlayer(actor);
    if (action) {
      return action;
    }
  }
};

export const getRandomOpen = (rng: typeof RNG, actor: Actor) => {
  const result: Record<string, Point> = {};
  const weights: Record<string, number> = {};
  const i = 0;
  for (const next of genAllDirections(actor)) {
    if (moveOut(next, actor) && !findActorForPosition(next.x, next.y)) {
      const dValue = gameData.dMapPlayer?.get(next);
      if (dValue) {
        result[i] = next;
        weights[i] = dValue;
      }
    }
  }
  const choice = rng.getWeightedValue(weights);
  if (choice) {
    return result[choice];
  }
};

export const addAttackParticle = (actor: Actor) => {
  actor.addParticle(
    ParticleManager.makeUntilStartOfTurn(
      AttackingParticle,
      actor.x,
      actor.y,
      actor.breed.fg
    )
  );
};

export const getDistance = (actor: Actor) => {
  return gameData.dMapPlayer?.get(actor);
};

export const getAdjacentToPlayer = (actor: Actor) => {
  return gameData.actors.filter((a) => {
    return (
      a !== actor &&
      a !== gameData.player &&
      Math.max(
        Math.pow(a.x - gameData.player.x, 2),
        Math.pow(a.y - gameData.player.y, 2)
      ) <= 1
    );
  });
};
